import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, ButtonBase, Grid, LinearProgress, Typography } from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import BlockIcon from '@mui/icons-material/Block';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import SubmissionAppService from '../../../../application/platform/ser/submission/submission-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { ISubmissionBatchResponse } from '../../../../model/ser/submission/response/submission-batch-response';
import { ISubmissionCancelBatchResponse } from '../../../../model/ser/submission/response/submission-cancel-batch-response';
import { formatDate, formatDateTime } from '../../../../utils/date';
import { SubmissionStatusChip } from '../../components/submission-chips';
import { CancelSubmissionDialog } from '../../components/cancel-submission-dialog';

interface BatchDetailDrawerProps {
    batchId?: number;
    refreshVersion: number;
    onClose: () => void;
    onOpenSubmission: (id: number) => void;
    onFilterByBatch: (batchId: number) => void;
    onChanged: () => void;
}

function plural(count: number, singular: string, pluralForm: string) {
    return `${count} ${count === 1 ? singular : pluralForm}`;
}

// Resumo do cancelamento parcial: "3 remessas canceladas, 1 não cancelada"
export function describeBatchCancel(result: ISubmissionCancelBatchResponse): string {
    const canceled = result.canceled?.length ?? 0;
    const ignored = result.ignored?.length ?? 0;
    const parts = [plural(canceled, 'remessa cancelada', 'remessas canceladas')];
    if (ignored) parts.push(plural(ignored, 'não cancelada', 'não canceladas'));
    return parts.join(', ');
}

export function BatchDetailDrawer({ batchId, refreshVersion, onClose, onOpenSubmission, onFilterByBatch, onChanged }: BatchDetailDrawerProps) {
    const { showError, showSuccess } = useFeedback();
    const [batch, setBatch] = useState<ISubmissionBatchResponse>();
    const [loading, setLoading] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelResult, setCancelResult] = useState<ISubmissionCancelBatchResponse>();

    useEffect(() => {
        setCancelResult(undefined);
    }, [batchId]);

    useEffect(() => {
        if (!batchId) {
            setBatch(undefined);
            return;
        }
        setLoading(true);
        SubmissionAppService.getBatch(batchId)
            .then(setBatch)
            .catch((error) => {
                const notFound = error?.originalError?.response?.status === 404;
                showError(notFound ? 'Lote não encontrado' : 'Não foi possível carregar o lote', notFound ? undefined : error);
                onClose();
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId, refreshVersion]);

    const submissions = batch?.submissions ?? [];
    const cancellable = submissions.filter((s) => s.canCancel).length;

    async function confirmCancel(reason: string) {
        if (!batch) return;
        setCancelling(true);
        try {
            const result = await SubmissionAppService.cancelBatch(batch.id, reason);
            setCancelResult(result);
            showSuccess(describeBatchCancel(result));
        } catch (error: any) {
            showError('Não foi possível cancelar o lote', error);
        } finally {
            setCancelling(false);
            setCancelOpen(false);
            onChanged();
        }
    }

    const footer = (
        <React.Fragment>
            {cancellable > 0 && (
                <Button color="error" variant="outlined" startIcon={<BlockIcon />} onClick={() => setCancelOpen(true)}>Cancelar lote</Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose}>Fechar</Button>
            <Button variant="contained" startIcon={<FilterListIcon />} disabled={!batch} onClick={() => batch && onFilterByBatch(batch.id)}>Ver na consulta</Button>
        </React.Fragment>
    );

    return (
        <React.Fragment>
            <FormDrawer
                open={batchId !== undefined}
                icon={<LayersIcon />}
                title={`Lote nº ${batchId ?? ''}`}
                subtitle={batch ? plural(batch.quantity, 'remessa', 'remessas') : undefined}
                onClose={onClose}
                footer={footer}
            >
                {loading && <LinearProgress />}
                {cancelResult && (
                    <Alert severity={cancelResult.ignored?.length ? 'warning' : 'success'} onClose={() => setCancelResult(undefined)}>
                        <AlertTitle>{describeBatchCancel(cancelResult)}</AlertTitle>
                        {(cancelResult.ignored ?? []).map((item) => (
                            <Typography key={item.id} variant="body2">Remessa nº {item.id}: {item.reason}</Typography>
                        ))}
                    </Alert>
                )}
                {batch && (
                    <React.Fragment>
                        <FormDrawerGroup title="Resumo">
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" component="div">Enviado em</Typography>
                                    <Typography variant="body2">{formatDateTime(batch.date)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" component="div">Remetente</Typography>
                                    <Typography variant="body2">{batch.userSender?.name || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" component="div">Entidade</Typography>
                                    <Typography variant="body2">{batch.clientName || '—'}</Typography>
                                </Grid>
                            </Grid>
                        </FormDrawerGroup>
                        <FormDrawerGroup title="Remessas do lote">
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                {submissions.map((item, index) => (
                                    <ButtonBase
                                        key={item.id}
                                        onClick={() => onOpenSubmission(item.id)}
                                        sx={{
                                            width: '100%', textAlign: 'left', p: 1.5, gap: 1.5, display: 'flex', alignItems: 'center',
                                            borderTop: index ? '1px solid' : 'none', borderColor: 'divider',
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>nº {item.id} · {item.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">Publicação em {formatDate(item.publishDate)}</Typography>
                                        </Box>
                                        <SubmissionStatusChip statusId={item.statusId} label={item.status} />
                                        <ChevronRightIcon color="action" />
                                    </ButtonBase>
                                ))}
                            </Box>
                        </FormDrawerGroup>
                    </React.Fragment>
                )}
            </FormDrawer>
            <CancelSubmissionDialog
                open={cancelOpen}
                title="Cancelar lote"
                summary={<Typography variant="body2"><strong>Lote nº {batch?.id}</strong>: {plural(cancellable, 'remessa ainda pode ser cancelada', 'remessas ainda podem ser canceladas')}. As demais serão mantidas.</Typography>}
                confirming={cancelling}
                onClose={() => setCancelOpen(false)}
                onConfirm={confirmCancel}
            />
        </React.Fragment>
    );
}
